import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { PsButtonTuner, PsCheckboxTuner, PsDropdown, PsTextTuner } from 'stencilSetup';
import {
  ChoosePCPDrawerFormContentStyled,
  MilesPsSelectTunerStyled,
} from './ChoosePCPDrawerStyled';
import { PlanDetailsMilesOptions } from 'enrollment/app/constants';
import { selectPCPProviderInfoLogic } from './SearchResultsPCPDrawer/services';

interface ChoosePCPDrawerFormProps {
  specialityValueOptions: any;
  handleLocationInput: (e: any) => void;
  locationInput: string;
  handleMilesInput: (e: any) => void;
  milesValue: number;
  providerNameValue: string;
  handleProviderNameInput: (e: any) => void;
  officeNameValue: string;
  handleOfficeNameInput: (e: any) => void;
  handleInNetworkChange: (e: any) => void;
  inNetworkValue: boolean;
  acceptNewPatientValue: boolean;
  handleAcceptNewPatient: (e: any) => void;
  handleSpecialityChange: (e: any) => void;
}

export const ChoosePCPDrawerForm = ({
  specialityValueOptions,
  handleLocationInput,
  locationInput,
  handleMilesInput,
  milesValue,
  providerNameValue,
  handleProviderNameInput,
  officeNameValue,
  handleOfficeNameInput,
  handleInNetworkChange,
  inNetworkValue,
  acceptNewPatientValue,
  handleAcceptNewPatient,
  handleSpecialityChange,
}: ChoosePCPDrawerFormProps) => {

  const { t } = useTranslation('enrollment.ChoosePCPDrawer');

  const [showMoreSearchCriteria, setShowMoreSearchCriteria] =
    useState<boolean>(false);
  const [searchCriteriaButtonLabel, setSearchCriteriaButtonLabel] = useState<string>(t('More Search Criteria'));

  const providerInfoLogicSelector = useSelector(selectPCPProviderInfoLogic);

  const specialityDropdownOptionsSelector =
    providerInfoLogicSelector?.specialityDropdownOptions;

  const handleMoreSearchCriteria = () => {
    setShowMoreSearchCriteria(!showMoreSearchCriteria);
    setSearchCriteriaButtonLabel(showMoreSearchCriteria ? t('More Search Criteria') : t('Less Search Criteria'));
  };

  const specialityValueOptionsCondition =
    Object.keys(specialityValueOptions).length > 0
      ? specialityValueOptions
      : specialityDropdownOptionsSelector;

  return (
    <div>
      <form>
        <ChoosePCPDrawerFormContentStyled className="">
          <div className="row">
            <div className="col-5 col-md-2 col-lg-2">
              <PsTextTuner
                data-testid="choose-pcp-location-input"
                inputId="choose-pcp-location-input"
                name="choose-pcp-location-input"
                label={t('dental.input.locationInputLabel')}
                type="text"
                value={locationInput}
                onInput={handleLocationInput}
                required
              />
            </div>
            <div className="col-2 ml-3 col-md-auto">
              <MilesPsSelectTunerStyled
                data-testid="choose-pcp-miles-select"
                inputId="choose-pcp-miles-select"
                label={t('dental.input.milesSelectLabel')}
                value={milesValue?.toString()}
                onPsChange={(e) => handleMilesInput(e.detail.event as any)}
                extraClass="pr-5"
              >
                {PlanDetailsMilesOptions?.map((milesOption: any) => {
                  return (
                    <option
                      key={`milesOptionKey-${milesOption.id}`}
                      id={`milesOption-${milesOption.id}`}
                      value={milesOption.value}
                    >
                      {milesOption.value}
                    </option>
                  );
                })}
              </MilesPsSelectTunerStyled>
            </div>
            <div className="col-md-3 ml-md-3 ">
              <PsTextTuner
                data-testid="choose-pcp-provider-input"
                inputId="choose-pcp-provider-input"
                name="choose-pcp-provider-input"
                label={t('dental.input.providerInputLabel')}
                type="text"
                value={providerNameValue}
                onInput={handleProviderNameInput}
                placeholder="Eg. Smith"
              />
            </div>
            <div className="col-md-3 ml-lg-3">
              <PsTextTuner
                data-testid="choose-pcp-office-input"
                inputId="choose-pcp-office-input"
                name="choose-pcp-office-input"
                label={t('dental.input.officeNameInputLabel')}
                type="text"
                value={officeNameValue}
                onInput={handleOfficeNameInput}
                placeholder="Eg. Coastal Dentistry"
              />
            </div>
          </div>
        </ChoosePCPDrawerFormContentStyled>
        {showMoreSearchCriteria && (
          <ChoosePCPDrawerFormContentStyled className="row">
            <div className="col-4">
              <PsDropdown
                selectId="choose-pcp-speciality-select"
                label="Speciality"
                multiple
                checkmarks
                offset="100,2"
                onClick={(e) => handleSpecialityChange(e)}
              >
                {specialityValueOptionsCondition?.length > 0 &&
                  specialityValueOptionsCondition?.map(
                    (providerOptions: any, index: any) => {
                      return (
                        <div
                          key={`providerOptionsKey-${index}`}
                          id={`providerOption-${index}`}
                        >
                          <span className="dropdown-item" role="button">
                            {providerOptions}
                          </span>
                        </div>
                      );
                    }
                  )}
              </PsDropdown>
            </div>
            <div className="col-auto">
              <PsCheckboxTuner
                data-testid="choose-pcp-in-network"
                inputId="choose-pcp-in-network"
                name="in-network"
                label="In Network"
                value={inNetworkValue}
                checked={inNetworkValue}
                onClick={handleInNetworkChange}
                extraClass="mt-4"
              />              
            </div>
            <div className="col-auto">
              <PsCheckboxTuner
                data-testid="choose-pcp-accept-new-patient"
                inputId="choose-pcp-accept-new-patient"
                name="accept-new-patient"
                label="Accepting New Patients"
                value={acceptNewPatientValue}
                checked={acceptNewPatientValue}
                onClick={handleAcceptNewPatient}
                extraClass="mt-4"
              />              
            </div>
          </ChoosePCPDrawerFormContentStyled>
        )}
      </form>
      <PsButtonTuner
        buttonId="more-search-criteria-btn"
        data-testid="more-search-criteria-btn"
        level="tertiary"
        label={searchCriteriaButtonLabel}
        extraClass="ml-n3 mt-5"
        show-underline
        onClick={handleMoreSearchCriteria}
      />
      <hr className="mr-1 ml-1 mb-4" />
    </div>
  );
};
